/**
 * Agent Architect API Service
 *
 * Handles all API calls for the Agent Architect visual workflow builder
 */
import axios from 'axios'

// Determine API URL - use env var if set, otherwise same-origin
const getApiBaseUrl = () => {
  const envUrl = import.meta.env.VITE_API_URL
  if (envUrl === '') return ''
  if (envUrl) {
    if (typeof window !== 'undefined' && window.location.protocol === 'https:' && envUrl.startsWith('http://')) {
      return envUrl.replace('http://', 'https://')
    }
    return envUrl
  }
  return 'http://localhost:8000'
}

const API_BASE_URL = getApiBaseUrl()

// Note: Auth headers are handled by the interceptor below

// Create axios instance
const api = axios.create({
  baseURL: `${API_BASE_URL}/api/v1/architect`,
  headers: { 'Content-Type': 'application/json' },
})

// Add auth interceptor
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// ==================== Types ====================

export interface WorkflowSummary {
  id: string
  name: string
  description?: string
  execution_engine: 'langgraph' | 'unified' | 'hybrid'
  node_count: number
  edge_count: number
  is_template: boolean
  is_public: boolean
  tags: string[]
  created_at: string
  updated_at: string
}

export interface WorkflowDetail extends WorkflowSummary {
  nodes: any[]
  edges: any[]
  config?: WorkflowConfig
  compiled_plan?: any
  version: string
}

export interface WorkflowConfig {
  quality_threshold: number
  max_revisions: number
  enable_parallel: boolean
  provider_mode?: string
}

export interface CreateWorkflowRequest {
  name: string
  description?: string
  nodes: any[]
  edges: any[]
  execution_engine?: 'langgraph' | 'unified' | 'hybrid'
  config?: WorkflowConfig
  is_template?: boolean
  is_public?: boolean
  tags?: string[]
}

export interface UpdateWorkflowRequest {
  name?: string
  description?: string
  nodes?: any[]
  edges?: any[]
  execution_engine?: 'langgraph' | 'unified' | 'hybrid'
  config?: WorkflowConfig
  is_template?: boolean
  is_public?: boolean
  tags?: string[]
}

export interface CompileWorkflowRequest {
  nodes: any[]
  edges: any[]
  execution_engine?: 'langgraph' | 'unified' | 'hybrid'
  config?: WorkflowConfig
}

export interface CompileResult {
  workflow_id: string
  is_valid: boolean
  validation_errors: string[]
  execution_plan?: any
  langgraph_config?: any
  code?: string
  mermaid_diagram?: string
}

export interface ExecuteWorkflowRequest {
  prompt: string
  quality_threshold?: number
  max_revisions?: number
}

export interface ExecutionResult {
  execution_id: string
  workflow_id: string
  status: 'pending' | 'running' | 'completed' | 'failed'
  final_output?: string
  task_results: Record<string, any>
  quality_score?: number
  total_cost: number
  duration_ms?: number
  error?: string
}

export interface WorkflowTemplate {
  id: string
  name: string
  description: string
  category: string
  nodes: any[]
  edges: any[]
  config?: any
}

// ==================== API Functions ====================

/**
 * List all workflows
 */
export async function listWorkflows(
  includeTemplates = false,
  includePublic = true
): Promise<WorkflowSummary[]> {
  const response = await api.get('/workflows', {
    params: { include_templates: includeTemplates, include_public: includePublic },
  })
  return response.data
}

/**
 * Get a specific workflow
 */
export async function getWorkflow(workflowId: string): Promise<WorkflowDetail> {
  const response = await api.get(`/workflows/${workflowId}`)
  return response.data
}

/**
 * Create a new workflow
 */
export async function createWorkflow(
  request: CreateWorkflowRequest
): Promise<WorkflowDetail> {
  const response = await api.post('/workflows', request)
  return response.data
}

/**
 * Update a workflow
 */
export async function updateWorkflow(
  workflowId: string,
  request: UpdateWorkflowRequest
): Promise<WorkflowDetail> {
  const response = await api.put(`/workflows/${workflowId}`, request)
  return response.data
}

/**
 * Delete a workflow
 */
export async function deleteWorkflow(workflowId: string): Promise<void> {
  await api.delete(`/workflows/${workflowId}`)
}

/**
 * Compile a workflow (without saving)
 */
export async function compileWorkflow(
  request: CompileWorkflowRequest
): Promise<CompileResult> {
  const response = await api.post('/compile', request)
  return response.data
}

/**
 * Compile a saved workflow
 */
export async function compileSavedWorkflow(
  workflowId: string
): Promise<CompileResult> {
  const response = await api.post(`/workflows/${workflowId}/compile`)
  return response.data
}

/**
 * Execute a workflow
 */
export async function executeWorkflow(
  workflowId: string,
  request: ExecuteWorkflowRequest
): Promise<ExecutionResult> {
  const response = await api.post(`/workflows/${workflowId}/execute`, request)
  return response.data
}

/**
 * List executions for a workflow
 */
export async function listWorkflowExecutions(
  workflowId: string,
  limit = 20
): Promise<ExecutionResult[]> {
  const response = await api.get(`/workflows/${workflowId}/executions`, {
    params: { limit },
  })
  return response.data
}

/**
 * Get workflow templates
 */
export async function getTemplates(): Promise<WorkflowTemplate[]> {
  const response = await api.get('/templates')
  return response.data
}

// ==================== Export default service object ====================

export const architectApi = {
  listWorkflows,
  getWorkflow,
  createWorkflow,
  updateWorkflow,
  deleteWorkflow,
  compileWorkflow,
  compileSavedWorkflow,
  executeWorkflow,
  listWorkflowExecutions,
  getTemplates,
}

export default architectApi
